import { SearchQuery, SearchOptions } from "../api"
import { FullConfig } from "../config"
import { Store } from "../state"
import pipe from "../utils/pipe"
import isPlainObject from "../utils/isPlainObject"
import ensureArray from "../utils/ensureArray"
import { createMapperOptions } from "./createMapperOptions"

export function mapParams(data: object, mapping: object) {
    return Object.entries(mapping)
        .sort((a, b) => b[0].split(".").length - a[0].split(".").length)
        .reduce<[object, object]>(
            ([parsed, source], [mapFrom, mapTo]) => {
                const value = objectGetByKey(source, mapFrom)
                // tslint:disable-next-line:no-null-keyword
                if (value != null) {
                    return [
                        // @ts-expect-error type mismatch
                        objectSetByKey(parsed, mapTo, value),
                        objectDeleteByKey(source, mapFrom)
                    ]
                }
                return [parsed, source]
            },
            [{}, data]
        )[0]
}

export function unmapParams(data: object, mapping: Record<string, string>) {
    if (Object.keys(mapping || {}).length > 0) {
        return mapParams(
            data,
            Object.entries(mapping).reduce(
                (prev, [key, value]) =>
                    Object.assign(Object.assign({}, prev), {
                        [value]: key
                    }),
                {}
            )
        )
    }
    return data
}

export function paramsToQueryString(params: Record<string, unknown>): string {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, values]) => {
        ensureArray(values)
            .filter(v => v != null)
            .forEach(v => {
                searchParams.append(key, String(v))
            })
    })
    return searchParams.toString()
}

export function queryStringToParams(query: string): Record<string, string[]> {
    const searchParams = new URLSearchParams(query)
    const params: Record<string, string[]> = {}
    searchParams.forEach((value, key) => {
        if (!params[key]) params[key] = []
        params[key]!.push(value)
    })
    return params
}

export function paramsToJson(params: Record<string, unknown>) {
    const data = Object.entries(params)
        .map(([k, v]) => ({
            key: k,
            values: ensureArray(v)
        }))
        .sort((a, b) => {
            return a.key < b.key ? -1 : a.key > b.key ? 1 : 0
        })
    return multikeyToJson(
        // @ts-expect-error type mismatch
        data.map(v => ({
            key: decodeKey(v.key),
            values: v.values
        }))
    )
}

export function jsonToParams(data: Record<string, unknown>, knownKeys: string[] = []): Record<string, unknown> {
    return Object.assign({}, ...Object.entries(data).map(([k, v]) => makeParams([k], v, knownKeys)))
}

export function updateUrlState(newUrl: string, title: string = document.title): void {
    const fullUrl = getFullUrl(newUrl)
    if (!compareUrl(window.location.href, fullUrl)) {
        window.history.pushState(undefined, title, fullUrl)
    }
}

export function bindUrlStateChange(callback: (event: PopStateEvent) => void): void {
    // Prevent popstate on page load
    setTimeout(() => {
        window.addEventListener("popstate", callback)
    }, 30)
}

export function compareUrl(url: string, otherUrl: string): boolean {
    return getFullUrl(url) === getFullUrl(otherUrl)
}

export function getFullUrl(url: string): string {
    return new URL(url.endsWith("?") ? url.slice(0, -1) : url, window.location.href).href
}

export function getSearchUrl(
    context: {
        store: Store
        config: FullConfig
    },
    query: SearchQuery,
    options?: SearchOptions
): string {
    const mapperOptions = createMapperOptions(context.config)
    const encoder = pipe(
        ...(context.config.customUrlMappings?.map(m => {
            // Wrap encoder to include mapperOptions
            return (query: unknown) => m.encode(query, mapperOptions)
        }) ?? [])
    )

    const paramsWithoutDefault = deepRemove(encoder(query), encoder(context.store.getInitialState().query))

    const params = jsonToParams(
        {
            ...(options?.customParams ?? {}),
            // @ts-expect-error type mismatch
            ...mapParams(paramsWithoutDefault, context.config.serpUrlMapping)
        },
        // apply array mapping only to known keys
        Object.values(context.config.serpUrlMapping)
    )

    return `${
        context.store.getInitialState()?.pageType === "search"
            ? (context.config.serpPath ?? location.pathname)
            : location.pathname
    }?${paramsToQueryString(params)}`
}

const KEY_SEPARATOR = "."
const KEY_SEPARATOR_ENCODED = "::"

interface MultiKeyItem {
    key: string[]
    values: Record<string, Record<string, unknown>>[]
}

interface GroupedMultiKeyItems {
    [key: string]: MultiKeyItem[]
}

function isInteger(a: string): boolean {
    return /^\+?(0|[1-9]\d*)$/.test(a)
}

function multikeyToJson(data: MultiKeyItem[]): Record<string, unknown> | object {
    if (!data.length) {
        return {}
    }
    const first = data[0]
    if (first!.key.length === 0) {
        if (first!.values.length === 1) {
            return first!.values[0]!
        }
        return first!.values
    }
    if (isInteger(first!.key[0]!)) {
        return Object.values(deeperMultikey(data))
    }
    return deeperMultikey(data)
}

function deeperMultikey(data: MultiKeyItem[]): Record<string, unknown> {
    const grouped = data.reduce<GroupedMultiKeyItems>((r, x) => {
        const k = x.key[0]
        return {
            ...r,
            [k!]: [...(r[k!] || []), x]
        }
    }, {})
    return Object.assign(
        {},
        ...Object.keys(grouped).map(key => ({
            [key]: multikeyToJson(cutFirstKey(grouped[key]!))
        }))
    )
}

function cutFirstKey(data: MultiKeyItem[]): MultiKeyItem[] {
    return data.map(v => ({
        key: v.key.slice(1),
        values: v.values
    }))
}

function makeParams(keys: string[], value: unknown, knownKeys: string[]): Record<string, unknown> {
    if (isPlainObject(value)) {
        return Object.assign({}, ...Object.entries(value).map(([k, v]) => makeParams([...keys, k], v, knownKeys)))
    }
    if (value instanceof Array && (knownKeys.includes(keys[0]!) || value.some(v => v instanceof Object))) {
        return Object.assign({}, ...value.map((v, i) => makeParams([...keys, i.toString()], v, knownKeys)))
    }
    return {
        [encodeKey(keys)]: value
    }
}

function encodeKey(keys: string[]): string {
    return keys.map(k => k.split(KEY_SEPARATOR).join(KEY_SEPARATOR_ENCODED)).join(KEY_SEPARATOR)
}

function decodeKey(key: string): string[] {
    return key.split(KEY_SEPARATOR).map(k => k.split(KEY_SEPARATOR_ENCODED).join(KEY_SEPARATOR))
}

export function objectDeleteByKey(obj: object, key: string) {
    obj = objectClone(obj)
    // tslint:disable-next-line:one-variable-per-declaration
    let i, index, len, pointer, subkey: string
    pointer = obj
    const subkeys = key.split(".")!
    for (index = i = 0, len = subkeys.length; i < len; index = ++i) {
        subkey = subkeys[index]!
        subkey = decodeURIComponent(subkey)
        if (isPlainObject(pointer) && subkey in pointer) {
            if (index + 1 === subkeys.length) {
                delete pointer[subkey]
            } else {
                pointer = pointer[subkey]
            }
        } else {
            break
        }
    }
    return obj
}

export function objectGetByKey(obj: object, key: string, defaultValue?: unknown) {
    // tslint:disable-next-line:one-variable-per-declaration
    let i, index, len, pointer, subkey: string, value
    pointer = obj
    const subkeys = key.split(".")
    value = void 0
    for (index = i = 0, len = subkeys.length; i < len; index = ++i) {
        subkey = subkeys[index]!
        subkey = decodeURIComponent(subkey)
        if (isPlainObject(pointer) && subkey in pointer) {
            pointer = pointer[subkey]
            value = pointer
        } else {
            value = defaultValue
            break
        }
    }
    return value
}

function objectClone<T>(obj: T): T
// eslint-disable-next-line no-redeclare
function objectClone(obj: unknown): unknown {
    if (obj instanceof Array) {
        return obj.map(v => objectClone(v))
    }
    if (isPlainObject(obj)) {
        return Object.entries(obj).reduce(
            (prev, [key, value]) =>
                Object.assign(Object.assign({}, prev), {
                    [objectClone(key)]: objectClone(value)
                }),
            {}
        )
    }
    return obj
}

export function objectSetByKey(obj: Record<string, unknown>, key: string, value: unknown) {
    obj = objectClone(obj)
    // tslint:disable-next-line:one-variable-per-declaration
    let i, index, len, pointer: Record<string, unknown>, subkey: string
    pointer = obj
    const subkeys = key.split(".")
    for (index = i = 0, len = subkeys.length; i < len; index = ++i) {
        subkey = subkeys[index]!
        subkey = decodeURIComponent(subkey)
        if (index + 1 !== subkeys.length) {
            if (!(subkey in pointer)) {
                pointer[subkey] = {}
            }
            if (!isPlainObject(pointer[subkey])) {
                pointer[subkey] = {}
            }
            // @ts-expect-error type mismatch
            pointer = pointer[subkey]
        } else {
            pointer[subkey] = value
        }
    }
    return obj
}

export function deepCompare(a: unknown, b: unknown) {
    return JSON.stringify(a) === JSON.stringify(b)
}

export function deepRemove<T>(value: T, removeValue: unknown): T
// eslint-disable-next-line no-redeclare
export function deepRemove(value: Record<string, unknown>, removeValue: unknown) {
    if (isPlainObject(value) && isPlainObject(removeValue)) {
        const result: Record<string, unknown> = {}
        Object.keys(value).forEach(key => {
            if (!deepCompare(value[key], removeValue[key])) {
                result[key] = deepRemove(value[key], removeValue[key])
            }
        })
        return result
    }
    return value
}
